import { AuthRedirectService } from '@edtech-ui/core/services/auth-redirect/auth-redirect.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { SharedModule } from '../../shared/shared.module';
import { AuthServiceService } from '../../core/services/authService/auth-service.service';
import { LoginPayload } from '../../core/models/interfaces/login';
import { Subscription } from 'rxjs';
import { isValidEmail } from '../../core/utils/validators';
import { CommonModule } from '@angular/common';
import { FireBaseServiceService } from '@edtech-ui/core/services/firebaseService/fire-base-service.service';
import { OneSignalService } from '@edtech-ui/core/services/one-signal-service/one-signal.service';

@Component({
	selector: 'app-user-registration-login-with-password',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, SharedModule],
	templateUrl: './user-registration-login-with-password.component.html',
	styleUrls: ['../user-registration-login-with-phone/user-registration-login-with-phone.component.scss']

})
export class UserRegistrationLoginWithPasswordComponent implements OnInit, OnDestroy {
	loginForm!: FormGroup;
	showPassword: boolean = false;
	showConfirmPassword: boolean = false;
	subscription = new Subscription();
	isPasswordFocused: boolean = false;
	isEmailFocused: boolean = false;
	isLoading: boolean = false;
	userEmail: string | null = null;
	fcmToken: string = '';
	redirectUrl: string = '';

	constructor(
		private router: Router,
		private _toastr: ToastrService,
		private _authService: AuthServiceService,
		private route:ActivatedRoute,
		private _fireBaseService:FireBaseServiceService,
		private _oneSignalService : OneSignalService,
		private _authRedirectService: AuthRedirectService
	) {
		this.loginForm = new FormGroup({
			email: new FormControl('', [
				Validators.required,
				Validators.email,
				(control: AbstractControl) => isValidEmail(control.value) ? null : { invalidEmailPattern: true }
			]),
			password: new FormControl('', [
				Validators.required,
				Validators.minLength(8),
			]),
		});
	}


	ngOnInit(): void {
		this.route.queryParamMap.subscribe(params => {
			this.redirectUrl = params.get('redirectUrl') || '';
			this._authRedirectService.redirectIfLoggedIn(this.redirectUrl);
		});
		this.userEmail = this._authService.getEmail();

		if (this.userEmail) {
			this.loginForm.patchValue({
				email: this.userEmail
			});
			this.isEmailFocused = true;
		}
		this.fcmToken = this._authService.getFcmToken() || '';
	}


	async onLogin() {
		if (this.loginForm.valid) {
			this.isLoading = true;

			const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown';
			const language = navigator.language || '';
			const country = language.includes('-') ? language.split('-')[1] : 'Unknown';
			const fcmToken = (await Promise.race([
				this._fireBaseService.getFCMToken(),
				new Promise((resolve) => setTimeout(() => resolve(''), 5000))
			])) as string || '';
		    const playerId = await this._oneSignalService.getPlayerId() || '';

			const loginPayload: LoginPayload = {
				email: this.loginForm.value.email,
				password: this.loginForm.value.password,
				fcm_token:fcmToken,
				is_default: true,
				login_source: "web",
				user_timezone: timeZone,
				country: country,
				onesignal_player_id:playerId
			};

			this.subscription.add(
				this._authService.postLogin(loginPayload).subscribe({
					next: (response) => {
						this.isLoading = false;

						if (!response.Success) {
							this._toastr.error(response.Message);
							return;
						}

						if (response.Data?.user_id) {
							const newUserId = response.Data.user_id.toString();
							localStorage.setItem('user_id', response.Data.user_id.toString());
							this._authService.currentUserId.next(newUserId);
							this._authService.currentUserId.next(response.Data.user_id.toString());
						}

						if (response.Data?.phone_email) {
							this._authService.currentUserEmail.next(response.Data.phone_email);
							localStorage.setItem('email', response.Data.phone_email);
						}

						if (response.Success && response.Data?.refresh_token && response.Data?.token) {
							if (response.Data?.role_id) {
								this._authService.setRoleId(response.Data.role_id.toString());
								localStorage.setItem('role_id', response.Data.role_id.toString());
							}
							this._authService.setToken(response.Data.token);
							localStorage.setItem('refresh_token', response.Data.refresh_token);
							this._toastr.success(response.Message);

							if (response.Data?.modules) {
								this._authService.setModules(response.Data.modules);
							}

							
							const pendingRoundId = localStorage.getItem('pending_practice_round_id');
							if (pendingRoundId && (response.Data.role_id === 2 || response.Data.role_id === 1)) {
								localStorage.removeItem('pending_practice_round_id');
								this.router.navigate(['/student-dashboard/ai-interview-dashboard']);
							} else {
								this._authRedirectService.handlePostLoginRedirect(this.redirectUrl);
							}
						} else {
							this.router.navigate(['/login']);
							this._toastr.error(response.Message);
						}
					},
					error: () => {
						this.isLoading = false;
						this._toastr.error('Server error occurred. Please try again.');
					}
				})
			);
		}
	}


	togglePassword(field: 'password' | 'confirmPassword') {
		if (field === 'password') {
			this.showPassword = !this.showPassword;
		} else {
			this.showConfirmPassword = !this.showConfirmPassword;
		}
	}


	onEmailFocus(): void {
		this.isEmailFocused = true;
	}

	onEmailBlur(): void {
		this.isEmailFocused = !!this.loginForm.controls['email'].value;
	}

	getEmailPlaceholder(): string {
		return this.isEmailFocused ? '' : 'email';
	}

	shouldFloatEmailLabel(): boolean {
		return this.isEmailFocused || !!this.loginForm.controls['email'].value;
	}

	onPasswordFocus(): void {
		this.isPasswordFocused = true;
	}

	onPasswordBlur(): void {
		this.isPasswordFocused = !!this.loginForm.controls['password'].value;
	}

	getPasswordPlaceholder(): string {
		return this.isPasswordFocused ? '' : 'Enter your password';
	}

	shouldFloatPasswordLabel(): boolean {
		return this.isPasswordFocused || !!this.loginForm.controls['password'].value;
	}

	goToLoginPage() {
		const email = this.loginForm.get('email')?.value;
		this.router.navigate(['/login-phone'], { queryParams: { email: email } });
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}